import React from 'react';

import cn from 'classnames/bind';
import { Checkbox } from '@consta/uikit/Checkbox';
import { useChoiceGroup } from '@consta/uikit/useChoiceGroup';
import styles from './styles.styl';
import { PropsWithHTMLAttributesAndRef } from '@consta/uikit/__internal__/src/utils/types/PropsWithHTMLAttributes';

const cx = cn.bind(styles);

export const checkboxGroupDirections = ['column', 'row'] as const;
export type CheckboxGroupDirection = typeof checkboxGroupDirections[number];
export const checkboxGroupDefaultDirection: CheckboxGroupDirection =
  checkboxGroupDirections[0];

export const checkboxGroupSizes = ['m', 'l'] as const;
export type CheckboxGroupPropSize = typeof checkboxGroupSizes[number];
export const checkboxGroupDefaultSize: CheckboxGroupPropSize =
  checkboxGroupSizes[0];

export const checkboxGroupViews = ['primary', 'ghost'] as const;
export type CheckboxGroupPropView = typeof checkboxGroupViews[number];
export const checkboxGroupDefaultView: CheckboxGroupPropView =
  checkboxGroupViews[0];

type CommonProps<ITEM> = {
  value?: ITEM[] | null;
  items: ITEM[];
  getLabel: (item: ITEM) => string;
  getKey: (item: ITEM) => number;
  getDisabled?: (item: ITEM) => boolean | undefined;
  onChange: (props: {
    e: React.ChangeEvent<HTMLInputElement>;
    value: ITEM[] | null;
  }) => void;
  name?: string;
  direction?: CheckboxGroupDirection;
  size?: CheckboxGroupPropSize;
  view?: CheckboxGroupPropView;
  disabled?: boolean;
  className?: string;
};

type Props<ITEM> = PropsWithHTMLAttributesAndRef<
  CommonProps<ITEM>,
  HTMLDivElement
>;

type CheckboxGroup = <ITEM>(props: Props<ITEM>) => React.ReactElement | null;

// eslint-disable-next-line react/display-name
export const CustomCheckboxGroup: CheckboxGroup = React.forwardRef(
  (props, ref) => {
    const {
      value = null,
      items,
      getLabel,
      getKey,
      getDisabled,
      onChange,
      name,
      direction = checkboxGroupDefaultDirection,
      size = checkboxGroupDefaultSize,
      view = checkboxGroupDefaultView,
      disabled = false,
      className,
      ...otherProps
    } = props;

    const { getOnChange, getChecked } = useChoiceGroup({
      value,
      getKey: getKey,
      callBack: onChange,
      multiple: true,
    });

    return (
      <div
        {...otherProps}
        ref={ref}
        className={cx(
          'CustomCheckboxGroup',
          `CustomCheckboxGroup__${direction}`,
          `CustomCheckboxGroup__${size}`,
          `CustomCheckboxGroup__${view}`,
          className,
        )}
      >
        {items.map((item) => (
          <Checkbox
            key={getKey(item)}
            label={getLabel(item)}
            size={size}
            view={view}
            name={name}
            disabled={disabled || getDisabled?.(item)}
            checked={getChecked(item)}
            onChange={({ e }) => getOnChange(item)(e)}
            className={cx(`CustomCheckboxGroup__${direction}_item`)}
          />
        ))}
      </div>
    );
  },
);
